import _ from "lodash";
import { C8AttributeId } from "src/c8-platform/type/node";
import ReflectionAttribute from "./ReflectionAttribute";
import ReflectionClass from "./ReflectionClass";
import ReflectionDomainAttribute from "./ReflectionDomainAttribute";
import ReflectionDomainRelation from "./ReflectionDomainRelation";
import { getClass } from "./reflectionData";
import { DomainClassData, IReflectionDomainAttribute } from "./reflectionTypes";

class ReflectionDomainClass {
	private _duplicateDestinations: Record<string, boolean>;

	readonly type: string;
	readonly primary: ReflectionClass;
	readonly secondaries: Record<string, ReflectionClass>;
	readonly attributes: Record<string, ReflectionDomainAttribute>;
	readonly outgoing: Record<string, ReflectionDomainRelation>;
	cardKeys: string[];

	getType(): string {
		return this.type;
	}

	/**
	 * Returns Domain Attribute with given id.
	 */
	getDomainAttribute = (id: string): IReflectionDomainAttribute => {
		return this.attributes[id] || this.outgoing[id];
	};

	/**
	 * Return list of all domain attributes that are not in given omit set (if provided)
	 */
	getDomainAttributes = (omitHidden?: boolean, omitSet?: Set<C8AttributeId>): ReflectionDomainAttribute[] => {
		let attributes = Object.values(this.attributes);

		if (omitHidden) {
			attributes = attributes.filter((da) => !da.rca.isHidden);
		}

		if (omitSet) {
			attributes = attributes.filter((da) => !omitSet.has(da.domainId));
		}

		return attributes;
	};

	/**
	 * Returns Reflection Class for Domain Attribute with given id.
	 */
	getDomainAttributeClass = (id: string): ReflectionClass => {
		const da = this.getDomainAttribute(id);
		if (da) {
			return da.secondaryId ? this.secondaries[da.secondaryId] : this.primary;
		}
		return null;
	};

	/**
	 * Return list of all domain relations that are not in given omit set (if provided)
	 */
	getDomainRelations = (omitHidden?: boolean, omitSet?: Set<C8AttributeId>): ReflectionDomainRelation[] => {
		let relations = Object.values(this.outgoing);

		if (omitHidden) {
			relations = relations.filter((dr) => !dr.rca.isHidden);
		}

		if (omitSet) {
			relations = relations.filter((dr) => !omitSet.has(dr.domainId));
		}

		return relations;
	};

	/**
	 * Returns destination Reflection Class for Domain Relation with given id.
	 */
	private getRelationDestinationClass = (id: string): ReflectionClass => {
		return this.outgoing[id]?.rca.getDestinationClass(this.getDomainAttributeClass(id));
	};

	/**
	 * Returns Reflection Attribute with given id.
	 */
	getAttribute = (id: string): ReflectionAttribute => {
		const da = this.getDomainAttribute(id);
		return da?.rca;
	};

	/**
	 * Returns Reflection Relation Id to Secondary Reflection Class if class is of the given type, or derived from that type.
	 */
	getSecondaryOfType = (type: string): string => {
		const rc = getClass(type);
		let id: string;
		_.some(this.secondaries, (rcSecondary, secondaryId) => {
			if (rcSecondary.isDerivedFrom(rc)) {
				id = secondaryId;
			}
			return !!id;
		});
		return id;
	};

	/**
	 * Return true if two or more visible relations in the domain target the given type. This only works if at
	 * least one of the relations is custom, as we have too many OOTB duplications where path names should not apply.
	 */
	hasDuplicateDestination = (type: string): boolean => {
		if (!this._duplicateDestinations) {
			const dups = this._duplicateDestinations = {};
			const dest = {};
			_.each(this.outgoing, (dr, id) => {
				const rca: ReflectionAttribute = dr.rca;
				if (rca.isSingle && rca.isEnabled && !rca.isHidden) {
					const rc = this.getRelationDestinationClass(id);
					if (rc && !rc.abstract) {
						const bCustom = rca.isCustom;
						const destType = rc.getType();
						if (dest[destType] || (bCustom && (dest[destType] === false))) {
							dups[destType] = 1;
						} else {
							dest[destType] = bCustom;
						}
					}
				}
			});
		}
		return this._duplicateDestinations[type];
	};

	constructor(data: DomainClassData) {
		this.type = data[0];
		this.primary = getClass(this.type);
		this.secondaries = {};
		this.attributes = {};
		this.outgoing = {};

		const { attributes, outgoing, primary, secondaries } = this;

		data[1]?.forEach((secondaryDef) => {
			const id = secondaryDef[0];
			secondaries[id] = getClass(secondaryDef[1]);
			primary.traverse(true, false, (rc) => {
				var rcr = rc.getRelation(id);
				if (rcr) {
					rcr.secondary = true;
				}
			}, secondaryDef);
		});

		primary.domainClass = this;

		_.each(secondaries, (rc) => {
			if (rc.domainClass === undefined) {
				rc.domainClass = this;
			} else if (rc.domainClass !== this) {
				rc.domainClass = null;
			}

			// mark class and all base classes as secondary as well
			rc.isSecondary = true;

			rc.superClasses.forEach((superClass) => {
				superClass.isSecondary = true;
			});
		});

		_.each(data[2], (attributeDef) => {
			const da = new ReflectionDomainAttribute(attributeDef, primary, secondaries);
			attributes[da.domainId] = da;
		});

		_.each(data[3], (relationDef) => {
			const da = new ReflectionDomainRelation(relationDef, primary, secondaries);
			outgoing[da.domainId] = da;
		});
	}
}

export = ReflectionDomainClass;